import { useTranslation } from 'react-i18next';
import DefaultTooltip from '../../../components/DefaultTooltip';
import QuestionIcon16pxSvg from '../../../assets/images/icons/QuestionIcon16pxSvg';
import CustomInput from '../../../components/CustomInput';
import RefreshCircleIcon20pxSvgV2 from '../../../assets/images/icons/RefreshCircleIcon20pxSvgV2';
import { CustomSecondaryButton, NormalSwitch, StyledDataGrid } from '../../../components/StyledComponents';
import TimezoneData from '../../../assets/data/TimezoneData';
import CustomSelect from '../../../components/CustomSelect';
import CustomTimePickerHaveSecondV2 from '../../../components/CustomTimePickerHaveSecondV2';
import { format } from 'date-fns';
import { generateTranslateMuiTable } from '../../../utils/Function/commonFunction';
import { useEffect, useMemo, useState } from 'react';
import { Tooltip } from '@mui/material';
import { ControlpanelDeleteButton, ControlpanelPrimaryButton, ControlpanelSecondaryButton } from '../../../components/StyledButton';
import NoRowOverlay from '../../../assets/images/icons/NoRowOverlay';
import CustomColumnsPanel from '../../../components/CustomColumnsPanel';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from "@mui/icons-material/Delete";
import ConfirmResetAllDialog from './ConfirmResetAllDialog';
import AddNewRecipientsDialog from './AddNewRecipientsDialog';
import EditRecipientsDialog from './EditRecipientsDialog';
import DeleteRecipientsDialog from './DeleteRecipientsDialog';
import DeleteAllRecipientsDialog from './DeleteAllRecipiensDialog';
import { getListRecipients } from '../../../axios/Setting/SettingUrlApi';
import EditNew20pxSvg from "../../../assets/images/icons/EditNew20pxSvg";
import DeleteIcon16pxSvg from '../../../assets/images/icons/DeleteIcon16pxSvg';

const SectionRow = ({
    title,
    switchName,
    checked,
    onChange,
    onReset,
    disabled,
    resetDisabled = false,
}) => {
    return (
        <div className="flex items-center justify-between py-[6px] w-full">
            {/* LEFT */}
            <div className="flex items-start gap-2">
                <p className="text-[14px]">{title}</p>
                <DefaultTooltip
                    title={title}
                    readMore={false}
                    content={
                        <div className="flex flex-col gap-3">
                            <p>{title}.</p>
                        </div>
                    }
                />
            </div>

            {/* RIGHT */}
            <div className="flex items-center gap-3">
                <NormalSwitch
                    name={switchName}
                    checked={checked}
                    onChange={onChange}
                    disabled={disabled}
                />

                {onReset && (
                    <CustomSecondaryButton
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();

                            if (resetDisabled) return;

                            onReset();
                        }}
                        className={`
            ${resetDisabled ? "pointer-events-none opacity-50" : ""}
        `}
                    >
                        <RefreshCircleIcon20pxSvgV2 />
                    </CustomSecondaryButton>
                )}
            </div>
        </div>
    );
};

const SystemConfigurationTab = ({ handleResetAll, refreshData, handleResetNoti, timeFormatData, dateFormatData, yearStartMonthData, monthStartDateData, weekStartDateData, settingKeyMap,
    handleDataChange, debouncedAutoSave, formData, handleInputChange, handleResetSetting, handleResetWebSetting, getAttrTypeId, getSettingTitle, getSettingEntityTitle }) => {

    const { t } = useTranslation();
    const timezoneData = TimezoneData();
    const tableTranslated = useMemo(() => generateTranslateMuiTable(t), [t]);
    const [openAddNewRecipients, setOpenAddNewRecipients] = useState(false);
    const [openEditRecipients, setOpenEditRecipients] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);
    const [openDeleteRecipients, setOpenDeleteRecipients] = useState(false);
    const [openDeleteAllRecipients, setOpenDeleteAllRecipients] = useState(false);
    const [recipientsData, setRecipientsData] = useState([]);
    const [confirmResetAllOpen, setConfirmResetAllOpen] = useState(false);

    const handleOpenConfirmResetAll = () => setConfirmResetAllOpen(true);

    const handleOpenAddNewRecipients = () => setOpenAddNewRecipients(true);
    const handleCloseAddNewRecipients = () => setOpenAddNewRecipients(false);

    const handleCloseEditRecipients = () => setOpenEditRecipients(false);

    const handleOpenDeleteRecipients = (row) => {
        setSelectedRow(row);
        setOpenDeleteRecipients(true);
    };

    const handleOpenAllDeleteRecipients = () => setOpenDeleteAllRecipients(true);

    const columnData = useMemo(
        () => [
            { field: "email", headerName: t("common.email"), flex: 1, minWidth: 180 },
            { field: "remark", headerName: t("common.description"), flex: 1, minWidth: 160 },
            {
                field: "options",
                headerName: t("common.options"),
                sortable: false,
                filterable: false,
                minWidth: 200,
                renderCell: (params) => (
                    <div
                        className="flex gap-2 items-center justify-start h-full"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* EDIT */}
                        <Tooltip title={t("button.edit")} arrow>
                            <button
                                type="button"
                                className="p-2 rounded-lg hover:bg-gray-100 transition cursor-pointer"
                                onClick={() => {
                                    setSelectedRow(params.row);
                                    setOpenEditRecipients(true);
                                }}
                            >
                                <EditNew20pxSvg className="w-5 h-5" />
                            </button>
                        </Tooltip>

                        {/* DELETE */}
                        <Tooltip title={t("button.delete")} arrow>
                            <button
                                type="button"
                                className="p-2 rounded-lg hover:bg-red-100 transition cursor-pointer"
                                onClick={() => handleOpenDeleteRecipients(params.row)}
                            >
                                <DeleteIcon16pxSvg className="w-5 h-5" color="#D00D00" />
                            </button>
                        </Tooltip>
                    </div>
                )
            },
        ],
        [t]
    );

    const fetchDataRecipients = async () => {
        const payload = {
            langId: localStorage.getItem("i18nextLngId"),
            // entityId: tabId,
        };
        try {
            const { data } = await getListRecipients(payload);
            const list = data?.data || [];
            setRecipientsData(list);
        } catch (error) {
            if (error?.response && error.response.status === 401) return;
            console.error("getListRecipients error:", error);
        }
    };

    useEffect(() => {
        fetchDataRecipients();
    }, [refreshData]);

    return (
        <>
            <div className="
  grid
  2xl:grid-cols-2
  grid-cols-1
  gap-0
">
                {/* LEFT */}
                <div className="2xl:pr-2">
                    <div className='bg-[#F9FAFB] p-4 rounded-[16px] space-y-2' data-tour="setting-time-region">
                        <h2 className='font-semibold'>{getSettingEntityTitle("Day start time", "common.timeAndRegion")}</h2>

                        {/* Dashboard Refresh Interval */}
                        <div className='space-y-1'>
                            <div className="flex items-center gap-1">
                                <p>{getSettingTitle("Dashboard refresh interval", "common.dashboard-refresh-interval")}</p>
                                <p>({t("common.in-minutes")})</p>

                                <DefaultTooltip
                                    title={getSettingTitle("Dashboard refresh interval", "common.dashboard-refresh-interval")}
                                    readMore={false}
                                    content={
                                        <div className="flex flex-col gap-3">
                                            <p>{t("common.dashboard-refresh-interval-des")}.</p>
                                        </div>
                                    }
                                ><div>
                                        <QuestionIcon16pxSvg className="fill-[#BDBDBD]" />
                                    </div>

                                </DefaultTooltip>
                            </div>

                            <div className="flex items-center gap-3">
                                <div className="flex-1"><CustomInput
                                    name="dashboardRefreshInterval"
                                    value={formData.dashboardRefreshInterval}
                                    handleInputChange={(e) => handleInputChange(e, "web")}
                                    inputType="number"
                                    className="flex-1 w-full"
                                    readOnly={false}
                                    minValue={0}
                                    maxValue={59}
                                    labelEnable={false}
                                /></div>


                                <CustomSecondaryButton
                                    onClick={() =>
                                        handleResetWebSetting(
                                            getAttrTypeId("Dashboard refresh interval"),
                                            "dashboardRefreshInterval"
                                        )
                                    }
                                >
                                    <RefreshCircleIcon20pxSvgV2></RefreshCircleIcon20pxSvgV2>
                                </CustomSecondaryButton>
                            </div>
                        </div>

                        <div className='space-y-1'>
                            <div className="flex items-center gap-2">
                                <p>{getSettingTitle("Timezone", "common.time-zone")}</p>
                                <DefaultTooltip
                                    title={getSettingTitle("Timezone", "common.time-zone")}
                                    readMore={false}
                                    content={
                                        <div className="flex flex-col gap-3">
                                            <p>{t("common.time-zone")}.</p>
                                        </div>
                                    }
                                >
                                    <div>
                                        <QuestionIcon16pxSvg className="fill-[#BDBDBD]" />
                                    </div>
                                </DefaultTooltip>
                            </div>

                            <div className="flex items-center gap-3">
                                <div className="flex-1">
                                    <CustomSelect
                                        name="timeZone"
                                        value={formData.timeZone}
                                        onChange={(e) => handleInputChange(e, "default")}
                                        options={timezoneData}
                                        className="w-full"
                                    />
                                </div>

                                <CustomSecondaryButton
                                    onClick={() =>
                                        handleResetSetting(
                                            getAttrTypeId("timeZone"),
                                            "timeZone"
                                        )
                                    }
                                >
                                    <RefreshCircleIcon20pxSvgV2></RefreshCircleIcon20pxSvgV2>
                                </CustomSecondaryButton>
                            </div>
                        </div>

                        <div className='space-y-1'>
                            <div className="flex items-center gap-2">
                                <p>{getSettingTitle("Day start time", "common.day-start-time")}</p>
                                <DefaultTooltip
                                    title={getSettingTitle("Day start time", "common.day-start-time")}
                                    readMore={false}
                                    content={
                                        <div className="flex flex-col gap-3">
                                            <p>{t("common.general-setting-question-1")}.</p>
                                        </div>
                                    }
                                >
                                    <QuestionIcon16pxSvg className="fill-[#BDBDBD]" />
                                </DefaultTooltip>
                            </div>

                            {/* Control */}
                            <div className="flex items-center gap-3">
                                <div className="flex-1">
                                    <CustomTimePickerHaveSecondV2
                                        hoursLabel={false}
                                        secondsEnabled={false}
                                        maxHours={23}
                                        name="dayStartTime"
                                        value={formData.dayStartTime}
                                        onTimeChange={(value) => {
                                            handleDataChange("dayStartTime", value);

                                            debouncedAutoSave(
                                                settingKeyMap.dayStartTime,
                                                value
                                            );
                                        }}
                                        className="w-full"
                                    />
                                </div>

                                <CustomSecondaryButton
                                    onClick={() =>
                                        handleResetSetting(
                                            getAttrTypeId("Day start time"),
                                            "dayStartTime"
                                        )
                                    }
                                >
                                    <RefreshCircleIcon20pxSvgV2></RefreshCircleIcon20pxSvgV2>
                                </CustomSecondaryButton>
                            </div>
                        </div>

                        <div className="space-y-1">
                            <div className="flex items-center gap-2">
                                <p>{getSettingTitle("Week start date", "common.week-start-date")}</p>
                                <DefaultTooltip
                                    title={getSettingTitle("Week start date", "common.week-start-date")}
                                    readMore={false}
                                    content={
                                        <div className="flex flex-col gap-3">
                                            <p>{t("common.general-setting-question-2")}.</p>
                                        </div>
                                    }
                                >
                                    <div>
                                        <QuestionIcon16pxSvg className="fill-[#BDBDBD]" />
                                    </div>
                                </DefaultTooltip>
                            </div>

                            <div className="flex items-center gap-3">
                                <div className="flex-1">
                                    <CustomSelect
                                        name="weekStartDate"
                                        value={formData.weekStartDate}
                                        onChange={(e) => handleInputChange(e, "default")}
                                        options={weekStartDateData}
                                        className="w-full"
                                    />
                                </div>

                                <CustomSecondaryButton
                                    onClick={() =>
                                        handleResetSetting(
                                            getAttrTypeId("Week start date"),
                                            "weekStartDate"
                                        )
                                    }
                                >
                                    <RefreshCircleIcon20pxSvgV2></RefreshCircleIcon20pxSvgV2>
                                </CustomSecondaryButton>
                            </div>
                        </div>

                        <div className="space-y-1">
                            <div className="flex items-center gap-2">
                                <p>{getSettingTitle("Month start date", "common.month-start-day")}</p>
                                <DefaultTooltip
                                    title={getSettingTitle("Month start date", "common.month-start-day")}
                                    readMore={false}
                                    content={
                                        <div className="flex flex-col gap-3">
                                            <p>{t("common.general-setting-question-3")}.</p>
                                        </div>
                                    }
                                >
                                    <div>
                                        <QuestionIcon16pxSvg className="fill-[#BDBDBD]" />
                                    </div>
                                </DefaultTooltip>
                            </div>

                            <div className="flex items-center gap-3">
                                <div className="flex-1">
                                    <CustomSelect
                                        name="monthStartDay"
                                        value={formData.monthStartDay}
                                        onChange={(e) => handleInputChange(e, "default")}
                                        options={monthStartDateData}
                                        className="w-full"
                                    />
                                </div>

                                <CustomSecondaryButton
                                    onClick={() =>
                                        handleResetSetting(
                                            getAttrTypeId("Month start date"),
                                            "monthStartDay"
                                        )
                                    }
                                >
                                    <RefreshCircleIcon20pxSvgV2></RefreshCircleIcon20pxSvgV2>
                                </CustomSecondaryButton>
                            </div>
                        </div>

                        <div className="space-y-1">
                            <div className="flex items-center gap-2">
                                <p>{getSettingTitle("Year start month", "common.year-start-month")}</p>
                                <DefaultTooltip
                                    title={getSettingTitle("Year start month", "common.year-start-month")}
                                    readMore={false}
                                    content={
                                        <div className="flex flex-col gap-3">
                                            <p>{t("common.general-setting-question-4")}.</p>
                                        </div>
                                    }
                                >
                                    <div>
                                        <QuestionIcon16pxSvg className="fill-[#BDBDBD]" />
                                    </div>
                                </DefaultTooltip>
                            </div>

                            <div className="flex items-center gap-3">
                                <div className="flex-1">
                                    <CustomSelect
                                        name="yearStartMonth"
                                        value={formData.yearStartMonth}
                                        onChange={(e) => handleInputChange(e, "default")}
                                        options={yearStartMonthData}
                                        className="w-full"
                                    />
                                </div>

                                <CustomSecondaryButton
                                    onClick={() =>
                                        handleResetSetting(
                                            getAttrTypeId("Year start month"),
                                            "yearStartMonth"
                                        )
                                    }
                                >
                                    <RefreshCircleIcon20pxSvgV2></RefreshCircleIcon20pxSvgV2>
                                </CustomSecondaryButton>
                            </div>
                        </div>

                        <div className="space-y-1">
                            <div className="flex items-center gap-2">
                                <p>{getSettingTitle("Date format", "common.date-format")}</p>
                                <p className="italic font-medium">
                                    {"( "}{t("common.example-date")}:{" "}
                                    {formData.dateFormat
                                        ? format(new Date(), formData.dateFormat)
                                        : "---"} {" )"}
                                </p>
                                <DefaultTooltip
                                    title={getSettingTitle("Date format", "common.date-format")}
                                    readMore={false}
                                    content={
                                        <div className="flex flex-col gap-3">
                                            <p>{t("common.general-setting-question-5")}.</p>
                                        </div>
                                    }
                                >
                                    <div>
                                        <QuestionIcon16pxSvg className="fill-[#BDBDBD]" />
                                    </div>
                                </DefaultTooltip>
                            </div>

                            <div className="flex items-start gap-3">
                                <div className="flex-1">
                                    <CustomSelect
                                        name="dateFormat"
                                        value={formData.dateFormat}
                                        onChange={(e) => handleInputChange(e, "default")}
                                        options={dateFormatData}
                                        className="w-full"
                                    />
                                </div>

                                <CustomSecondaryButton
                                    onClick={() =>
                                        handleResetSetting(getAttrTypeId("Date format"), "dateFormat")
                                    }
                                >
                                    <RefreshCircleIcon20pxSvgV2></RefreshCircleIcon20pxSvgV2>
                                </CustomSecondaryButton>
                            </div>
                        </div>

                        <div className="space-y-1">
                            <div className="flex items-center gap-2">
                                <p>{getSettingTitle("Time format", "common.time-format")}</p>
                                <p className="italic">
                                    {"( "}{t("common.example-time")}:{" "}
                                    {formData.timeFormat
                                        ? format(new Date(), formData.timeFormat)
                                        : format(new Date(), "HH:mm:ss")}{" )"}
                                </p>
                                <DefaultTooltip
                                    title={getSettingTitle("Time format", "common.time-format")}
                                    readMore={true}
                                    content={
                                        <div className="flex flex-col gap-3">
                                            <p>{t("common.general-setting-question-7")}.</p>
                                        </div>
                                    }
                                >
                                    <div>
                                        <QuestionIcon16pxSvg className="fill-[#BDBDBD]" />
                                    </div>
                                </DefaultTooltip>
                            </div>

                            <div className="flex items-start gap-3">
                                <div className="flex-1">
                                    <CustomSelect
                                        name="timeFormat"
                                        value={formData.timeFormat}
                                        onChange={(e) => handleInputChange(e, "default")}
                                        options={timeFormatData}
                                        className="w-full"
                                    />
                                </div>

                                <CustomSecondaryButton
                                    onClick={() =>
                                        handleResetSetting(getAttrTypeId("Time format"), "timeFormat")
                                    }
                                >
                                    <RefreshCircleIcon20pxSvgV2></RefreshCircleIcon20pxSvgV2>
                                </CustomSecondaryButton>
                            </div>
                        </div>

                        <div className="space-y-1">
                            {/* Label */}
                            <div className="flex items-center gap-2">
                                <p>{getSettingTitle("Data Report Push Time", "common.data-report-push-time")}</p>
                                <DefaultTooltip
                                    title={getSettingTitle("Data Report Push Time", "common.data-report-push-time")}
                                    readMore={false}
                                    content={
                                        <div className="flex flex-col gap-3">
                                            <p>{t("common.data-report-des")}.</p>
                                        </div>
                                    }
                                >
                                    <div>
                                        <QuestionIcon16pxSvg className="fill-[#BDBDBD]" />
                                    </div>
                                </DefaultTooltip>
                            </div>

                            {/* Control */}
                            <div className="flex items-center gap-3">
                                <div className="flex-1">
                                    <CustomTimePickerHaveSecondV2
                                        name="dataReportPushTime"
                                        value={formData.dataReportPushTime}
                                        // handleInputChange={handleInputChange}
                                        onTimeChange={(value) => {
                                            handleDataChange("dataReportPushTime", value);

                                            debouncedAutoSave(
                                                settingKeyMap.dataReportPushTime,
                                                value
                                            );
                                        }}
                                        hoursLabel={false}
                                        secondsEnabled={false}
                                        maxHours={23}
                                        className="w-full"
                                        readOnly={true}
                                    />
                                </div>

                                <CustomSecondaryButton
                                    onClick={() =>
                                        handleResetSetting(
                                            getAttrTypeId("Data Report Push Time"),
                                            "dataReportPushTime"
                                        )
                                    }
                                    readOnly={true}
                                    className='pointer-events-none opacity-50'
                                >
                                    <RefreshCircleIcon20pxSvgV2></RefreshCircleIcon20pxSvgV2>
                                </CustomSecondaryButton>
                            </div>
                        </div>
                    </div>

                    <div className='mt-4 bg-[#F9FAFB] p-4 rounded-[16px] space-y-2' data-tour="setting-invitation">
                        <h2 className='font-semibold'>{getSettingEntityTitle("Url invite to join company", "button.invitation")}</h2>

                        <div className="space-y-1">
                            <div className="flex items-center gap-2">
                                <p>{getSettingTitle("Url invite to join company", "common.url-invite-to-join-company")}</p>
                                <DefaultTooltip
                                    title={getSettingTitle("Url invite to join company", "common.url-invite-to-join-company")}
                                    readMore={false}
                                    content={
                                        <div className="flex flex-col gap-3">
                                            <p>{t("common.url-des")}.</p>
                                        </div>
                                    }
                                >
                                    <div>
                                        <QuestionIcon16pxSvg className="fill-[#BDBDBD]" />
                                    </div>
                                </DefaultTooltip>
                            </div>

                            <div className="flex items-center gap-3">
                                <div className="flex-1">
                                    <CustomInput
                                        name="urlInviteToJoinCompany"
                                        value={formData.urlInviteToJoinCompany}
                                        onChange={(e) => handleInputChange(e, "default")}
                                        inputType="text"
                                        className="w-full"
                                        readOnly={false}
                                        minValue={-1}
                                        labelEnable={false}
                                    />
                                </div>

                                <CustomSecondaryButton
                                    onClick={() =>
                                        handleResetSetting(
                                            getAttrTypeId("Unidentified eventlog count to keep."),
                                            "urlInviteToJoinCompany"
                                        )
                                    }
                                >
                                    <RefreshCircleIcon20pxSvgV2></RefreshCircleIcon20pxSvgV2>
                                </CustomSecondaryButton>
                            </div>
                        </div>

                        <div className="space-y-1">
                            <div className="flex items-center gap-2">
                                <p>{getSettingTitle("Invite email valid time", "common.invite-email-valid-time")}</p>
                                <DefaultTooltip
                                    title={getSettingTitle("Invite email valid time", "common.invite-email-valid-time")}
                                    readMore={false}
                                    content={
                                        <div className="flex flex-col gap-3">
                                            <p>{t("common.invite-time")}.</p>
                                        </div>
                                    }
                                >
                                    <div>
                                        <QuestionIcon16pxSvg className="fill-[#BDBDBD]" />
                                    </div>
                                </DefaultTooltip>
                            </div>

                            <div className="flex items-center gap-3">
                                <div className="flex-1">
                                    <CustomInput
                                        name="inviteEmailValidTime"
                                        value={formData.inviteEmailValidTime}
                                        onChange={(e) => handleInputChange(e, "default")}
                                        inputType="number"
                                        className="w-full"
                                        readOnly={false}
                                        // minValue={-1}
                                        labelEnable={false}
                                    />
                                </div>

                                <CustomSecondaryButton
                                    onClick={() =>
                                        handleResetSetting(
                                            getAttrTypeId("Invite email valid time"),
                                            "inviteEmailValidTime"
                                        )
                                    }
                                >
                                    <RefreshCircleIcon20pxSvgV2></RefreshCircleIcon20pxSvgV2>
                                </CustomSecondaryButton>
                            </div>
                        </div>
                    </div>
                </div>

                {/* RIGHT */}
                <div className="2xl:pl-2">
                    <div className='bg-[#F9FAFB] p-4 rounded-[16px] space-y-2' data-tour="setting-notification">
                        <h2 className='font-semibold'>{getSettingEntityTitle("SMS Notifications", "common.notifications")}</h2>

                        <div className="flex items-center justify-between py-2 gap-6">
                            <div className="flex items-center gap-2">
                                <p className="">
                                    {getSettingTitle("Notify check-in attempts on restricted leave", "common.notify-check-in-attempts-on-restricted-leave")}
                                </p>

                                <DefaultTooltip
                                    title={getSettingTitle("Notify check-in attempts on restricted leave", "common.notify-check-in-attempts-on-restricted-leave")}
                                    readMore={false}
                                    content={
                                        <div className="flex flex-col gap-3">
                                            <p>{t("common.general-setting-question-11")}.</p>
                                        </div>
                                    }
                                >
                                    <QuestionIcon16pxSvg className="fill-[#BDBDBD]" />
                                </DefaultTooltip>
                            </div>

                            {/* RIGHT: Enable + Switch */}
                            <div className="flex items-center gap-4 shrink-0">
                                <NormalSwitch
                                    name="notifyCheckinAttemptsOnRestrictedLeave"
                                    checked={formData.notifyCheckinAttemptsOnRestrictedLeave}
                                    onChange={(e) => {
                                        handleInputChange(e, "default")
                                    }}
                                    disabled
                                />

                                <CustomSecondaryButton
                                    onClick={() =>
                                        handleResetSetting(
                                            getAttrTypeId("Notify check-in attempts on restricted leave"),
                                            "notifyCheckinAttemptsOnRestrictedLeave"
                                        )
                                    }
                                    className='pointer-events-none opacity-50'
                                >
                                    <RefreshCircleIcon20pxSvgV2></RefreshCircleIcon20pxSvgV2>
                                </CustomSecondaryButton>
                            </div>
                        </div>

                        <div className="w-full">
                            <SectionRow
                                title={getSettingTitle("SMS Notifications", "common.sms-notifications")}
                                switchName="smsNotification" // ✅ đúng key
                                checked={false}
                                onChange={(e) => {
                                    handleInputChange(e, "noti")
                                }}
                                onReset={() =>
                                    handleResetNoti(   // ✅ dùng reset noti
                                        getAttrTypeId("SMS Notifications"),
                                        "smsNotification"
                                    )
                                }
                                disabled
                                resetDisabled
                            />
                        </div>

                        <div className="w-full">

                            <div className="flex flex-col min-w-0">
                                <SectionRow
                                    title={getSettingTitle("Send mail nofication", "common.email-notifications")}
                                    switchName="sendmailnotification"
                                    checked={formData.sendmailnotification}
                                    onChange={(e) => {
                                        handleInputChange(e, "noti")
                                    }}
                                    onReset={() =>
                                        handleResetNoti(
                                            getAttrTypeId("Send mail nofication"),
                                            "sendmailnotification"
                                        )
                                    }
                                />


                                {formData.sendmailnotification && (
                                    <div className="pl-2"><SectionRow
                                        title={getSettingTitle("Send mail to company admin", "common.send-mail-to-company-admin")}
                                        switchName="sendmailtocompanyadmin"
                                        checked={formData.sendmailtocompanyadmin}
                                        onChange={(e) => {
                                            handleInputChange(e, "noti")
                                        }}
                                        onReset={() =>
                                            handleResetNoti(
                                                getAttrTypeId("Send mail to company admin"),
                                                "sendmailtocompanyadmin"
                                            )
                                        }
                                    />
                                        <SectionRow
                                            title={getSettingTitle("Send mail to group admin", "common.send-mail-to-group-admin")}
                                            switchName="sendmailtogroupadmin"
                                            checked={formData.sendmailtogroupadmin}
                                            onChange={(e) => {
                                                handleInputChange(e, "noti")
                                            }}
                                            onReset={() =>
                                                handleResetNoti(
                                                    getAttrTypeId("Send mail to group admin"),
                                                    "sendmailtogroupadmin"
                                                )
                                            } /><SectionRow
                                            title={getSettingTitle("Send mail to another email address", "common.send-mail-to-another-email-address")}
                                            switchName="sendmailtoanotheremailaddress"
                                            checked={formData.sendmailtoanotheremailaddress}
                                            onChange={(e) => {
                                                handleInputChange(e, "noti")
                                            }}
                                            onReset={() =>
                                                handleResetNoti(
                                                    getAttrTypeId("Send mail to another email address"),
                                                    "sendmailtoanotheremailaddress"
                                                )} />

                                        {/* DataGrid container becomes full-width, responsive height */}
                                        {formData.sendmailtoanotheremailaddress && (
                                            <div className="mt-3 w-full min-w-0">
                                                <div className="w-full overflow-hidden rounded-xl border border-[#E5E7EB] bg-white">
                                                    <StyledDataGrid
                                                        showColumnVerticalBorder
                                                        sx={{
                                                            maxWidth: "100%",
                                                            height: "49.8vh",
                                                            border: "none",
                                                            backgroundColor: "#FFFFFF",
                                                            "&": {
                                                                "--DataGrid-footerHeight": "0px",
                                                                "--DataGrid-scrollbarSize": "0px",
                                                            },
                                                            "& .MuiDataGrid-main": {
                                                                backgroundColor: "#FFFFFF",
                                                                // Border lấy từ wrapper ngoài — tránh bị filler cắt cạnh phải
                                                                border: "none",
                                                                borderRadius: 0,
                                                            },
                                                            "& .MuiDataGrid-virtualScroller": {
                                                                backgroundColor: "#FFFFFF",
                                                            },
                                                            "& .MuiDataGrid-columnHeaders": {
                                                                backgroundColor: "#FFFFFF",
                                                                paddingRight: "0 !important",
                                                            },
                                                            "& .MuiDataGrid-topContainer": {
                                                                paddingRight: "0 !important",
                                                            },
                                                            "& .MuiDataGrid-scrollbarFiller": {
                                                                display: "none !important",
                                                            },
                                                            "& .MuiDataGrid-footerContainer": {
                                                                display: "none",
                                                            },
                                                        }}
                                                        onRowClick={(params) => setSelectedRow(params.row)}
                                                        rows={recipientsData.map((item, idx) => ({
                                                            id: item.id,
                                                            index: idx + 1,
                                                            email: item.email,
                                                            remark: item.remark,
                                                        }))}
                                                        getRowId={(r) => r.id}
                                                        columns={columnData}
                                                        slots={{ noRowsOverlay: NoRowOverlay, columnsPanel: CustomColumnsPanel, }}
                                                        localeText={tableTranslated}
                                                        disableRowSelectionOnClick
                                                        hideFooterPagination
                                                    />
                                                </div>

                                                {/* Action buttons: stack on mobile */}
                                                <div className="flex flex-col sm:flex-row gap-2 justify-between pt-3">
                                                    <ControlpanelDeleteButton
                                                        disabled={!recipientsData || recipientsData.length === 0}
                                                        onClick={handleOpenAllDeleteRecipients}
                                                    >
                                                        <p>{t("common.delete-all")}</p>
                                                    </ControlpanelDeleteButton>
                                                    <ControlpanelPrimaryButton onClick={handleOpenAddNewRecipients}>
                                                        <p>{t("button.add")}</p>
                                                    </ControlpanelPrimaryButton>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>

                    </div>
                </div>
            </div >

            {confirmResetAllOpen && (
                <ConfirmResetAllDialog onClose={() => setConfirmResetAllOpen(false)} onConfirm={handleResetAll} />
            )}

            <AddNewRecipientsDialog open={openAddNewRecipients} handleClose={handleCloseAddNewRecipients} onRefresh={fetchDataRecipients} />

            <EditRecipientsDialog open={openEditRecipients} handleClose={handleCloseEditRecipients} data={selectedRow} onRefresh={fetchDataRecipients} />

            <DeleteRecipientsDialog open={openDeleteRecipients} onClose={() => setOpenDeleteRecipients(false)} data={selectedRow} onRefresh={fetchDataRecipients} />

            <DeleteAllRecipientsDialog open={openDeleteAllRecipients} onClose={() => setOpenDeleteAllRecipients(false)} onRefresh={fetchDataRecipients} />
        </>
    )
}

export default SystemConfigurationTab